/* ============================================================
 * Vistas del rol Aleph: red, clientes agregados, alta y edición de sedes.
 * ============================================================ */

import React, { useState, useEffect } from 'react';
import { useCtx, useRecurso, useAccion, api } from '../estado.jsx';
import { Kpi, Recurso, SedeTabs } from './Comunes.jsx';
import { Fotos } from './Fotos.jsx';
import { clp, toMin } from '../utiles/fechas.js';

/* ---------- Visión global ---------- */

export function GlobalView() {
  const { go } = useCtx();
  const metricas = useRecurso(() => api.metricas(), []);
  const agregado = useRecurso(() => api.clientesAgregado(), []);

  return (
    <div className="view on">
      <div className="h-eyebrow">Aleph · Todas las sedes · Chile</div>
      <h1 className="h-title">Visión <em>global</em></h1>
      <p className="h-sub">Qué está pasando hoy en toda la red.</p>

      <Recurso estado={metricas} que="las métricas de la red">
        {({ demanda, porSede }) => {
          const totalHoy = porSede.reduce((a, s) => a + s.reservas_hoy, 0);
          const totalTunes = porSede.reduce((a, s) => a + s.tunes, 0);
          const ocupMedia = porSede.length
            ? Math.round(porSede.reduce((a, s) => a + s.ocupacion, 0) / porSede.length) : 0;
          // La escala se toma del máximo real. Si aún no hay reservas, se usa
          // 1 para no dividir por cero y pintar todas las barras a cero.
          const maxDemanda = Math.max(1, ...demanda.map((d) => d.v));

          return (
            <>
              <div className="kpis">
                <Kpi label="Reservas hoy" val={totalHoy} foot="en toda la red" />
                <Kpi label="Ocupación media" val={ocupMedia} sufijo="%" foot="de la capacidad" />
                <Kpi label="Clientes únicos"
                     val={agregado.datos ? agregado.datos.unicos : '…'}
                     foot="en la red · por teléfono" />
                <Kpi label="Equipos TUNE" val={totalTunes} foot="activos en la red" />
              </div>

              <h2 className="section-h">Demanda por franja horaria</h2>
              <p className="section-note">
                Reservas de los últimos 30 días agregadas por hora de inicio.{' '}
                <b>Materia prima de la capa de inteligencia de Aleph</b>: sobre esta curva
                se construyen la predicción de demanda y la optimización de protocolos.
              </p>
              <div className="card"><div className="vchart">
                {demanda.map((d) => (
                  <div key={d.h} className={'vbar' + (d.v === maxDemanda && d.v > 0 ? ' peak' : '')}>
                    <div className="col" style={{ height: Math.round(d.v / maxDemanda * 100) + '%' }} />
                    <div className="hr">{d.h}</div>
                  </div>
                ))}
              </div></div>

              <h2 className="section-h">Sedes</h2>
              <div className="card"><table className="stable">
                <thead><tr>
                  <th>Sede</th><th className="num">TUNE</th><th className="num">Reservas hoy</th>
                  <th className="num">Semana</th><th className="num">Ocupación</th>
                  <th className="num">Altas 7d</th><th />
                </tr></thead>
                <tbody>{porSede.map((s) => (
                  <tr key={s.id}>
                    <td><div className="s-comuna">{s.comuna}</div><div className="s-name">{s.nombre}</div></td>
                    <td className="num">{s.tunes}</td>
                    <td className="num">{s.reservas_hoy}</td>
                    <td className="num">{s.reservas_semana}</td>
                    <td className="num"><span className="pill">{s.ocupacion}%</span></td>
                    <td className="num">{s.nuevos_semana}</td>
                    <td className="num">
                      <button className="tlink" onClick={() => go('sede', s.id)}>Ver ›</button>
                    </td>
                  </tr>
                ))}</tbody>
              </table></div>
            </>
          );
        }}
      </Recurso>
    </div>
  );
}

/* ---------- Clientes agregados ---------- */

export function ClientesGlobalView() {
  const agregado = useRecurso(() => api.clientesAgregado(), []);

  return (
    <div className="view on">
      <div className="h-eyebrow">Aleph · Clientes de la red</div>
      <h1 className="h-title">Clientes <em>agregados</em></h1>
      <p className="h-sub">
        Información confidencial: Aleph la ve solo en agregado, sin nombres. El <b>teléfono</b>{' '}
        (y el <b>ID de TUNE</b>) enlazan a la misma persona entre sedes.
      </p>

      <Recurso estado={agregado} que="los agregados">
        {(d) => (
          <>
            <div className="kpis">
              <Kpi label="Clientes únicos" val={d.unicos} foot="por teléfono en la red" />
              <Kpi label="Fichas" val={d.fichas} foot="suma de todas las sedes" />
              <Kpi label="En más de una sede" val={d.en_varias_sedes} foot="misma persona, varias sedes" />
              <Kpi label="Con bono activo" val={d.con_bono} foot="saldo disponible" />
            </div>
            <h2 className="section-h">Clientes por sede</h2>
            <p className="section-note">
              Solo recuentos. Cada sede ve únicamente a sus clientes y no sabe de qué otro centro
              vienen; el cruce entre sedes solo existe aquí, en Aleph.
            </p>
            <div className="card"><table className="stable">
              <thead><tr><th>Sede</th><th className="num">Fichas</th><th className="num">Con bono</th></tr></thead>
              <tbody>{d.porSede.map((s) => (
                <tr key={s.id}>
                  <td><div className="s-comuna">{s.comuna}</div><div className="s-name">{s.nombre}</div></td>
                  <td className="num">{s.fichas}</td>
                  <td className="num">{s.con_bono}</td>
                </tr>
              ))}</tbody>
            </table></div>
          </>
        )}
      </Recurso>
    </div>
  );
}

/* ---------- Editar sede ---------- */

export function EditSedeView() {
  const { sedeId, go, openModal } = useCtx();
  const sede = useRecurso(() => api.sede(sedeId), [sedeId]);
  const { ejecutar, enCurso, error, errorDatos } = useAccion();
  const [guardado, setGuardado] = useState('');

  useEffect(() => {
    if (errorDatos?.reservasFuera) openModal({ tipo: 'bloqueo', datos: errorDatos });
  }, [errorDatos, openModal]);

  return (
    <div className="view on">
      <SedeTabs id={sedeId} active="editsede" />
      <Recurso estado={sede} que="la sede">
        {(s) => (
          <>
            <FormularioEditarSede
              sede={s} enCurso={enCurso} error={error} errorDatos={errorDatos} guardado={guardado}
              alGuardar={(datos) => ejecutar(
                () => api.editarSede(sedeId, datos),
                () => { setGuardado('Guardado ✓'); setTimeout(() => setGuardado(''), 1800); })}
              alVolver={() => go('sede', sedeId)} />
            {/*
              Aleph da de alta un centro antes de que exista la cuenta de su
              operador, así que también le pone las fotos: si no, la ficha
              pública nacería vacía y habría que esperar a que alguien de la
              sede pudiera entrar.
            */}
            <Fotos fotos={s.fotos || []} sedeId={sedeId} />
            <BajaSede sede={s} sedeId={sedeId} alEliminar={() => go('global')} />
          </>
        )}
      </Recurso>
    </div>
  );
}

function FormularioEditarSede({ sede, alGuardar, enCurso, error, errorDatos, guardado }) {
  const [nombre, setNombre] = useState(sede.nombre);
  const [direccion, setDireccion] = useState(sede.direccion);
  const [precio, setPrecio] = useState(sede.precio_tune);
  // La base devuelve TIME como "09:00:00" y el input type=time quiere "09:00".
  const hhmm = (t) => (t || '').slice(0, 5);
  const [apertura, setApertura] = useState(hhmm(sede.hora_apertura));
  const [cierre, setCierre] = useState(hhmm(sede.hora_cierre));
  const [bloque, setBloque] = useState(sede.bloque_min);
  const activos = (sede.tunes || []).filter((t) => t.estado !== 'retirado').length;
  const [count, setCount] = useState(activos);
  const [errLocal, setErrLocal] = useState('');

  const guardar = () => {
    // Se comprueba aquí además de en el servidor para no gastar un viaje en
    // un error que se ve a simple vista.
    if (cierre <= apertura) {
      setErrLocal('El cierre tiene que ser posterior a la apertura.');
      return;
    }
    setErrLocal('');
    alGuardar({
      nombre: nombre.trim(),
      direccion: direccion.trim(),
      precioTune: parseInt(precio, 10) || sede.precio_tune,
      horaApertura: apertura,
      horaCierre: cierre,
      bloqueMin: parseInt(bloque, 10) || sede.bloque_min,
      numTunes: count,
    });
  };

  return (
    <>
      <div className="h-eyebrow">Aleph · Editar sede</div>
      <h1 className="h-title">Editar <em>{sede.comuna}</em></h1>
      <p className="h-sub">Datos que gobierna Aleph. La sede no puede cambiarlos.</p>
      <div className="alta-grid">
        <div className="alta-col">
          <div className="group-label" style={{ marginTop: 0 }}>Datos</div>
          <div className="field full"><label>Nombre</label>
            <input className="inp" value={nombre} onChange={(e) => setNombre(e.target.value)} /></div>
          <div className="field full"><label>Dirección</label>
            <input className="inp" value={direccion} onChange={(e) => setDireccion(e.target.value)} /></div>
          <div className="field full"><label>Precio de la sesión TUNE (CLP)</label>
            <input className="inp" type="number" min="0" step="500" value={precio}
                   onChange={(e) => setPrecio(e.target.value)} /></div>
          {/*
            El correo de acceso ya no se cambia desde aquí. Un centro puede
            tener varios, y este campo los reescribía todos a la vez con el
            mismo valor. El relevo se hace en Accesos: se autoriza el nuevo y
            se retira el anterior, que además deja rastro de quién operó y
            hasta cuándo.
          */}
          <p className="tune-hint" style={{ marginTop: 14 }}>
            Los correos que operan este centro se dan de alta y se retiran
            desde <b>Accesos</b>.
          </p>
          <div className="inline-err show">{errLocal || (errorDatos?.reservasFuera ? '' : error)}</div>
        </div>
        <div className="alta-col">
          <div className="group-label" style={{ marginTop: 0 }}>Horario</div>
          <p className="tune-hint" style={{ marginBottom: 14 }}>
            Define la rejilla de reservas del centro. Cambiarlo <b>no toca las reservas
            ya hechas</b>: solo los huecos que se ofrecerán a partir de ahora.
          </p>
          <div className="field"><label>Abre</label>
            <input className="inp" type="time" value={apertura}
                   onChange={(e) => setApertura(e.target.value)} /></div>
          <div className="field"><label>Cierra</label>
            <input className="inp" type="time" value={cierre}
                   onChange={(e) => setCierre(e.target.value)} /></div>
          <div className="field full"><label>Duración del bloque (minutos)</label>
            <input className="inp" type="number" min="5" max="120" step="5" value={bloque}
                   onChange={(e) => setBloque(e.target.value)} /></div>
        </div>
        <div className="alta-col">
          <div className="group-label" style={{ marginTop: 0 }}>Equipos TUNE</div>
          <p className="tune-hint" style={{ marginBottom: 14 }}>
            Dar de alta o retirar equipos es decisión de Aleph. <b>No se pueden retirar equipos
            con reservas futuras confirmadas</b>: hay que liberarlas o trasladarlas antes.
          </p>
          <div className="tune-count">
            <div className="stepper">
              <button type="button" onClick={() => setCount((c) => Math.max(1, c - 1))}>−</button>
              <div className="count">{count}</div>
              <button type="button" onClick={() => setCount((c) => Math.min(30, c + 1))}>+</button>
            </div>
            <p className="tune-hint">Total de TUNE en esta sede.</p>
          </div>
        </div>
        <div className="alta-actions">
          <button className="btn" onClick={guardar} disabled={enCurso}>
            {enCurso ? 'Guardando…' : 'Guardar cambios'}
          </button>
          <span className="saved">{guardado}</span>
        </div>
      </div>
    </>
  );
}

/* ---------- Alta de sede ---------- */

export function AltaSedeView() {
  const { go, refrescar } = useCtx();
  const { ejecutar, enCurso, error } = useAccion();
  const [f, setF] = useState({
    nombre: '', comuna: '', ciudad: '', direccion: '',
    ap: '09:00', ci: '19:00', bloque: 20, precio: 25000, email: '',
  });
  const [count, setCount] = useState(3);
  const [errLocal, setErrLocal] = useState('');
  const [creada, setCreada] = useState(null);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const bloquesDia = (() => {
    const mins = toMin(f.ci) - toMin(f.ap);
    return mins > 0 ? Math.floor(mins / (f.bloque || 20)) : 0;
  })();

  // El original no daba de alta nada: montaba el SQL como texto para copiarlo
  // a mano. El backend ya crea la sede, sus equipos y el acceso en una sola
  // transacción, así que aquí se llama al endpoint y se acabó.
  const enviar = () => {
    if (!f.nombre.trim() || !f.comuna.trim() || !f.direccion.trim()) {
      setErrLocal('Completa nombre, comuna y dirección.'); return;
    }
    if (!/^[^\s@]+@gmail\.com$/i.test(f.email.trim())) {
      setErrLocal('El acceso debe ser una cuenta @gmail.com.'); return;
    }
    setErrLocal('');
    ejecutar(
      () => api.crearSede({
        nombre: f.nombre.trim(),
        comuna: f.comuna.trim(),
        direccion: f.direccion.trim() + (f.ciudad.trim() ? ', ' + f.ciudad.trim() : ''),
        horaApertura: f.ap, horaCierre: f.ci,
        bloqueMin: parseInt(f.bloque, 10) || 20,
        precioTune: parseInt(f.precio, 10) || 0,
        numTunes: count,
        emailAcceso: f.email.trim(),
      }),
      (salida) => { setCreada(salida); refrescar(); },
    );
  };

  if (creada) {
    return (
      <div className="view on">
        <div className="h-eyebrow">Aleph · Administración</div>
        <h1 className="h-title">Sede <em>creada</em></h1>
        <p className="h-sub">
          Se ha creado la sede con sus {count} equipos y se ha autorizado {f.email} para operarla.
        </p>
        <div className="alta-actions">
          <button className="btn" onClick={() => go('sede', creada.id)}>Ir a la sede</button>
          <button className="btn ghost" style={{ marginLeft: 10 }}
                  onClick={() => { setCreada(null); setF({ ...f, nombre: '', comuna: '', direccion: '', email: '' }); }}>
            Dar de alta otra
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="view on">
      <div className="h-eyebrow">Aleph · Administración</div>
      <h1 className="h-title">Dar de alta una <em>sede</em></h1>
      <p className="h-sub">Registra el local, define cuántos TUNE tiene y el correo Google que la opera.</p>
      <div className="alta-grid">
        <div className="alta-col">
          <div className="group-label" style={{ marginTop: 0 }}>Acceso al panel</div>
          <p className="access-note">
            La sede entra con <b>Google</b> usando una cuenta <b>@gmail.com</b> que autorizas aquí.
            Solo este correo podrá operar esta sede.
          </p>
          <div className="field full"><label>Correo Gmail autorizado</label>
            <input className="inp" type="email" value={f.email} onChange={set('email')}
                   placeholder="sedenunoa@gmail.com" /></div>

          <div className="group-label">Datos de la sede</div>
          <div className="field full"><label>Nombre</label>
            <input className="inp" value={f.nombre} onChange={set('nombre')} placeholder="TUNE Ñuñoa" /></div>
          <div className="frow">
            <div className="field"><label>Comuna</label>
              <input className="inp" value={f.comuna} onChange={set('comuna')} placeholder="Ñuñoa" /></div>
            <div className="field"><label>Ciudad / Región</label>
              <input className="inp" value={f.ciudad} onChange={set('ciudad')} placeholder="Santiago" /></div>
          </div>
          <div className="field full"><label>Dirección</label>
            <input className="inp" value={f.direccion} onChange={set('direccion')}
                   placeholder="Av. Irarrázaval 1234" /></div>
          <div className="frow">
            <div className="field"><label>Horario</label>
              <div className="time-pair">
                <input className="inp" type="time" value={f.ap} onChange={set('ap')} />
                <span>a</span>
                <input className="inp" type="time" value={f.ci} onChange={set('ci')} />
              </div></div>
            <div className="field"><label>Bloque (min)</label>
              <input className="inp" type="number" min="5" step="5" value={f.bloque}
                     onChange={set('bloque')} /></div>
          </div>
          <div className="field full"><label>Precio de la sesión TUNE (CLP)</label>
            <input className="inp" type="number" min="0" step="500" value={f.precio}
                   onChange={set('precio')} /></div>
          <div className="inline-err show">{errLocal || error}</div>
        </div>

        <div className="alta-col">
          <div className="group-label" style={{ marginTop: 0 }}>Equipos TUNE</div>
          <div className="tune-count">
            <div className="stepper">
              <button type="button" onClick={() => setCount((c) => Math.max(1, c - 1))}>−</button>
              <div className="count">{count}</div>
              <button type="button" onClick={() => setCount((c) => Math.min(30, c + 1))}>+</button>
            </div>
            <p className="tune-hint">Número de TUNE = <b>sesiones simultáneas</b> por bloque.</p>
          </div>
          <div className="preview" style={{ marginTop: 26 }}>
            <div className="pv-label">Resumen · unidades a crear</div>
            <div className="chips">
              {Array.from({ length: count }, (_, i) => i + 1).map((i) => (
                <span key={i} className="chip"><span className="cdot" />TUNE {i}</span>
              ))}
            </div>
            <p className="pv-cap">
              Con <b>{count} TUNE</b> y bloques de <b>{f.bloque} min</b>, hasta{' '}
              <b>{bloquesDia * count} sesiones/día</b> ({bloquesDia} bloques × {count}).
            </p>
          </div>
        </div>

        <div className="alta-actions">
          <button className="btn" onClick={enviar} disabled={enCurso}>
            {enCurso ? 'Creando…' : 'Dar de alta'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Baja de un centro.
 *
 * Dos operaciones muy distintas y a propósito separadas. Desactivar lo saca
 * del escaparate y es reversible; eliminar borra en cascada equipos,
 * clientes, accesos, fotos, servicios, bonos y reservas. El servidor solo
 * permite lo segundo cuando no hay historial que perder, así que aquí no se
 * simula la comprobación: se intenta y se enseña lo que responda.
 */
function BajaSede({ sede, sedeId, alEliminar }) {
  const { openModal } = useCtx();
  const { ejecutar, enCurso, error, errorDatos } = useAccion();
  const [confirmando, setConfirmando] = useState(false);
  const [aviso, setAviso] = useState('');

  // El rechazo por historial se explica en modal, con la alternativa a mano.
  useEffect(() => {
    if (errorDatos && (errorDatos.clientes != null || errorDatos.reservas != null)) {
      setConfirmando(false);
      openModal({ tipo: 'noEliminable', datos: { ...errorDatos, sedeId } });
    }
  }, [errorDatos, openModal, sedeId]);

  const cambiarActiva = (activa) => ejecutar(
    () => api.editarSede(sedeId, { activa }),
    (r) => setAviso(activa
      ? 'Centro reactivado. Ya vuelve a aparecer en el escaparate.'
      : `Centro desactivado. Ya no aparece en el escaparate.${
          r?.reservasFuturas ? ` Ojo: quedan ${r.reservasFuturas} reserva(s) futura(s) confirmada(s); esas personas se presentarán igual.` : ''
        }`));

  const eliminar = () => ejecutar(() => api.eliminarSede(sedeId), () => alEliminar());

  return (
    <>
      <h2 className="section-h">Dar de baja</h2>
      <p className="section-note">
        <b>Desactivar</b> retira el centro del escaparate y del buscador, pero conserva
        clientes, reservas y bonos. Se puede deshacer.<br />
        <b>Eliminar</b> borra el centro y todo lo que cuelga de él. Solo es posible si
        todavía no tiene clientes ni reservas.
      </p>

      <div className="alta-actions" style={{ gap: 10 }}>
        {sede.activa !== false ? (
          <button className="btn ghost" disabled={enCurso} onClick={() => cambiarActiva(false)}>
            Desactivar centro
          </button>
        ) : (
          <button className="btn ghost" disabled={enCurso} onClick={() => cambiarActiva(true)}>
            Reactivar centro
          </button>
        )}

        {confirmando ? (
          <>
            <span className="tune-hint" style={{ marginRight: 6 }}>
              ¿Eliminar «{sede.nombre}» definitivamente?
            </span>
            <button className="btn" disabled={enCurso} onClick={eliminar}>
              {enCurso ? 'Eliminando…' : 'Sí, eliminar'}
            </button>
            <button className="btn ghost" disabled={enCurso} onClick={() => setConfirmando(false)}>
              Cancelar
            </button>
          </>
        ) : (
          <button className="btn ghost" disabled={enCurso} onClick={() => { setAviso(''); setConfirmando(true); }}>
            Eliminar centro
          </button>
        )}
      </div>

      {aviso ? <div className="section-note" style={{ marginTop: 10 }}>{aviso}</div> : null}
      <div className="inline-err show">{errorDatos?.clientes != null ? '' : error}</div>
    </>
  );
}
